/* eslint-disable @next/next/no-img-element */

import {Fragment} from 'react';

type CTACopy = {
  title: string;
  content: string;
};

export const ctaLifestyleSummary: CTACopy = {
  title: 'What does my TallyAge™ reveal about my lifestyle?',
  content:
    'Scientific evidence suggests you can improve your epigenetic age. Learn how your lifestyle plays a vital role.',
};

export const ctaActionPlan: CTACopy = {
  title: 'How our Tally Health Action Plans work',
  content:
    'Understand how your Action Plans are calculated to provide the best possible recommendations curated for you.',
};

type PageContentListItem = {
  imageUrl: string;
  title: React.ReactNode;
  content: React.ReactNode;
};

type PageContentList = {
  type: 'list';
  items: PageContentListItem[];
};

type PageContentFormattedText = {
  type: 'formattedText';
  content: React.ReactNode;
};

export type PageContent = PageContentList | PageContentFormattedText;

type PageAccordion = {
  analyticsName?: string;
  title: React.ReactNode;
  contents: PageContent[];
};

type PageReference = {
  authors: React.ReactNode;
  title: React.ReactNode;
  url: string;
};

type PageCopy = {
  analyticsName?: string;
  title: React.ReactNode;
  subTitle: React.ReactNode;
  contents: PageContent[];
  accordions: PageAccordion[];
  references: PageReference[];
};

const Refs: React.FC<{ns: number[]}> = ({ns}) => (
  <span className="refs">
    [
    {ns.map((n, index) => (
      <Fragment key={n}>
        <a className="ref" href={`#ref-${n}`}>
          {n}
        </a>
        {index < ns.length - 1 ? ', ' : ''}
      </Fragment>
    ))}
    ]
  </span>
);

export const lifestylesummary: PageCopy = {
  analyticsName: 'LS what this means',
  title: 'What does my TallyAge™ reveal about my lifestyle?',
  subTitle: 'Ideally, your epigenetic age will be lower than your chronological age.',
  contents: [
    {
      type: 'formattedText',
      content: (
        <>
          <img alt="" src="/image/tally-explorer/father-and-son.png" />
          <p>
            Ideally, your epigenetic age will be lower than your chronological age. A recent,
            well-designed study published in the journal, Genetics, showed that less than 10% of
            longevity is heritable – meaning that much of how we age is based on lifestyle choices
            and environment. Interestingly, less than 10% of how you age is determined by genetics.
            Diet, sleep habits, exercise, stress management, and other factors each play an
            important role <Refs ns={[1, 2]} />. But if your epigenetic age is older than your
            chronological age, don’t worry. Scientific evidence suggests you can improve your
            epigenetic age.
          </p>
          <p>
            While it can take as little as eight weeks to see changes in one’s epigenetic age with
            specific lifestyle interventions <Refs ns={[3]} />, we recommend testing each quarter
            (every three months) to see how your lifestyle changes and daily supplementation are
            influencing your TallyAge™ over time. Eventually, you will see a trendline that is
            indicative of your overall progress.
          </p>
        </>
      ),
    },
  ],
  accordions: [],
  references: [
    {
      authors:
        'Johnson, A.A., English, B.W., Shokhirev, M.N., Sinclair, D.A., Cuellar, T.L. (2022).',
      title: 'Human  age reversal: Fact or fiction? Aging Cell, 21(8), :e13664.',
      url: 'https://onlinelibrary.wiley.com/doi/10.1111/acel.13664',
    },
    {
      authors:
        'Ruby, J. G., Wright, K. M., Rand, K. A., Kermany, A., Noto, K., Curtis, D., Varner, N., … & Pritchard, J. K. (2018).',
      title:
        'Estimates of the heritability of human longevity are substantially inflated due to assortative mating. Genetics, 210(3), 1109–1124.',
      url: 'https://academic.oup.com/genetics/article/210/3/1109/5931081',
    },
    {
      authors:
        'Fitzgerald, K. N., Hodges, R., Hanes, D., Stack, E., Cheishvili, D., Szyf, M., Henkel, J., Twedt, M. W., … & Bradley, R. (2021).',
      title:
        'Potential reversal of epigenetic age using a diet and lifestyle intervention: A pilot randomized clinical trial. Aging, 13(7), 9419–9432.',
      url: 'https://www.aging-us.com/article/202913/text',
    },
  ],
};

export const actionplan: PageCopy = {
  analyticsName: 'AP How this works',
  title: 'How our Tally Health Action Plans work',
  subTitle: 'Our custom Action Plans put you in control.',
  contents: [
    {
      type: 'formattedText',
      content: (
        <>
          <img alt="" src="/image/tally-explorer/petri-dish.png" />
          <p>
            Our custom Action Plans put you in control. Our team of scientists have reviewed the
            most advanced research to provide you with data-driven recommendations indexed against
            the perceived difficulty of change. You can then choose the plan that works best for
            you, while we keep you on track and help you stay accountable with regular TallyAge™
            testing and weekly check-ins.
          </p>
          <p>
            Most importantly, we’ll predict the highest-value changes you can make to your various
            lifestyle choices based on your unique DNA methylation and lifestyle patterns driven by
            changes others like you are making. And, we’ll show you how a few small changes could be
            just as impactful as one larger one, to give you options that are most appropriate for
            you.
          </p>
          <p>
            Curious to see what else can help you lower your TallyAge™? Consider a Tally Health
            membership to receive quarterly TallyAge™ tests, a full personalized Action Plan based
            on your DNA methylation and lifestyle, educational content, and our unique daily
            longevity supplement, Vitality. Your DNA is not your destiny — join our mission to live
            healthier, longer at <a href="https://www.tallyhealth.com">www.tallyhealth.com</a>.
          </p>
        </>
      ),
    },
  ],
  accordions: [],
  references: [],
};

/* eslint-enable @next/next/no-img-element */
